import * as React from 'react'

import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Collapse,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { useGetSettingsStructure } from 'services/queries/use-get-settings-structure'
import SettingsKeys from './settings-keys'
import { usePutUserSettings } from 'services/queries/use-put-user-settings'
import { useDeleteUserSettings } from 'services/queries/use-delete-user-settings'
import Loading from './new-user-setting.loading'

type TableNameT = 'credit_check' | 'company_info' | 'monitor_table' | 'credit_check_sidebar' | ''

// Map technical table names to human-readable titles
const tableNameLabels: Record<string, string> = {
  credit_check: 'Credit Check',
  company_info: 'Company Info',
  monitor_table: 'Monitor Table',
  credit_check_sidebar: 'Credit Check Sidebar',
  fraud_indicators: 'Fraud Indicators',
}

function NewUserSetting() {
  const { data, isLoading: isFetching } = useGetSettingsStructure()
  const { mutate: addNewSetting, isLoading: isUpdating } = usePutUserSettings()
  const { mutate: deleteUserSetting, isLoading: isDeleting } =
    useDeleteUserSettings()
  const isLoading = isFetching || isUpdating || isDeleting
  const [addKey, setIsAddKey] = React.useState<TableNameT>('')
  const [newFieldValue, setNewFieldValue] = React.useState('')
  const [isVisible, setIsVisible] = React.useState(false)

  const handleDeleteKey = (key: string) => {
    const inferredTableName = data?.find((table) => {
      return table.keys.includes(key)
    })?.tableName

    deleteUserSetting({
      tableName: inferredTableName,
      key,
    })
  }

  const handleOpenNewKeyField = (tableName: TableNameT) => {
    setIsAddKey(tableName)
    setNewFieldValue('')
    setIsVisible(false)
  }

  const handleCancelAddKey = () => {
    setIsAddKey('')
    setNewFieldValue('')
    setIsVisible(false)
  }

  const handleAddNewKey = () => {
    if (addKey !== '' && newFieldValue.trim() !== '') {
      addNewSetting({
        key: newFieldValue,
        visibility: isVisible,
        tableName: addKey,
      })
      setIsAddKey('')
      setNewFieldValue('')
    }
  }

  if (isLoading) return <Loading />

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        {data?.map((structure) => {
          const isAddingToThisTable = addKey === structure.tableName

          return (
            <Paper
              key={structure?.tableName}
              variant="outlined"
              sx={{ borderRadius: 2, overflow: 'hidden' }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: 'action.hover',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {tableNameLabels[structure?.tableName] || structure?.tableName}
                </Typography>
                <Button
                  onClick={() => handleOpenNewKeyField(structure.tableName as TableNameT)}
                  variant="text"
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: 'none' }}
                >
                  Add key
                </Button>
              </Box>

              <Box sx={{ p: 2 }}>
                <Collapse in={isAddingToThisTable}>
                  <Paper
                    variant="outlined"
                    sx={(theme) => ({
                      p: 2,
                      mb: 2,
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(84, 189, 203, 0.08)'
                          : 'primary.lighter',
                      borderColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(84, 189, 203, 0.3)'
                          : 'primary.light',
                      borderRadius: 1.5,
                    })}
                  >
                    <Stack spacing={2}>
                      <TextField
                        onChange={(e) => setNewFieldValue(e.target.value)}
                        value={newFieldValue}
                        fullWidth
                        size="small"
                        placeholder="Enter new key name..."
                        variant="outlined"
                        autoFocus
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: (theme) =>
                              theme.palette.mode === 'dark'
                                ? '#2D2D2D'
                                : 'background.paper',
                          },
                        }}
                      />

                      <FormControl size="small">
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                          Default visibility
                        </Typography>
                        <RadioGroup
                          row
                          value={isVisible}
                          onChange={() => setIsVisible(!isVisible)}
                        >
                          <FormControlLabel
                            value={false}
                            control={<Radio size="small" />}
                            label={<Typography variant="body2">Hidden</Typography>}
                          />
                          <FormControlLabel
                            value={true}
                            control={<Radio size="small" />}
                            label={<Typography variant="body2">Visible</Typography>}
                          />
                        </RadioGroup>
                      </FormControl>

                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          onClick={handleCancelAddKey}
                          variant="outlined"
                          size="small"
                          startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleAddNewKey}
                          variant="contained"
                          size="small"
                          disabled={!newFieldValue.trim()}
                          startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
                        >
                          Add
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                </Collapse>

                <SettingsKeys
                  keys={structure?.keys || []}
                  onDelete={(key) => handleDeleteKey(key)}
                />
              </Box>
            </Paper>
          )
        })}
      </Stack>
    </Box>
  )
}

export default NewUserSetting
